import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

// CSS стили для hover эффектов и прокрутки.
// Подсветка подсказок и трек скроллбара выдержаны в токенах редизайна.
const suggestionStyles = `
  .suggestion-item:hover {
    background-color: #f6faff !important;
  }

  .custom-suggestions-container {
    overflow-y: auto !important;
    overflow-x: hidden !important;
    max-height: 320px !important;
  }

  .custom-suggestions-container::-webkit-scrollbar {
    width: 6px !important;
  }

  .custom-suggestions-container::-webkit-scrollbar-track {
    background: #eef2f8 !important;
    border-radius: 3px !important;
  }

  .custom-suggestions-container::-webkit-scrollbar-thumb {
    background: #c0ccdc !important;
    border-radius: 3px !important;
  }

  .custom-suggestions-container::-webkit-scrollbar-thumb:hover {
    background: #7a8aa3 !important;
  }
`;

// Добавляем стили в head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = suggestionStyles;
  document.head.appendChild(styleSheet);
}

// Функция для удаления дубликатов и лишних записей
const removeDuplicates = (suggestions) => {
  if (!suggestions || suggestions.length === 0) return [];

  const seen = new Set();
  const filtered = [];

  for (const suggestion of suggestions) {
    const address = suggestion.data;

    // Формируем отображаемый адрес
    const displayParts = [
      address.city_with_type,
      address.settlement_with_type,
      address.street_with_type,
      address.house,
      address.block
    ].filter(Boolean).filter(item => item.trim() !== '');

    const displayAddress = displayParts.join(', ');

    // Пропускаем если адрес пустой или уже был
    if (!displayAddress || seen.has(displayAddress)) {
      continue;
    }

    // Пропускаем адреса которые являются подстрокой других более детальных адресов
    const isSubstring = filtered.some(existingSuggestion => {
      const existingData = existingSuggestion.data;
      const existingParts = [
        existingData.city_with_type,
        existingData.settlement_with_type,
        existingData.street_with_type,
        existingData.house,
        existingData.block
      ].filter(Boolean).filter(item => item.trim() !== '');

      const existingAddress = existingParts.join(', ');

      // Если текущий адрес содержится в существующем более детальном адресе
      return existingAddress.includes(displayAddress) && existingAddress !== displayAddress;
    });

    if (!isSubstring) {
      seen.add(displayAddress);
      filtered.push(suggestion);
    }
  }

  return filtered;
};

// - Функция сортировки Suggestions (Приоритет: МСК, СПБ, КРД)
const sortSuggestionsByPriority = (suggestions, selectedRegion, query) => {
  // - Приоритетные города 
  const PRIORITY_CITIES = ["москва", "санкт-петербург", "краснодар"];

  // - Обработка запроса (убираем пробелы и приводим к нижнему регистру)
  const queryProcessed = query.toLowerCase();

  // - Получаем название города по его коду 
  const selectedCity = {
    "МСК": "москва",
    "СПБ": "санкт-петербург",
    "КРД": "краснодар"
  }[selectedRegion];

  // - Сортируем 
  // - suggestions приводим к массиву для сортировки 
  return [...suggestions].sort((first, second) => {
    const firstCity = (first.data.city || "").toLowerCase();
    const secondCity = (second.data.city || "").toLowerCase();

    // - Функция определения приоритета 
    const priority = (city) => {
      if (selectedCity && city.includes(selectedCity)) return 3;
      if (city === queryProcessed) return 2;
      if (PRIORITY_CITIES.some(s => city.includes(s))) return 1;

      return 0;
    };

    return priority(secondCity) - priority(firstCity);

  });

};

const CustomAddressAutocomplete = ({
  value,
  onChange,
  placeholder = "Введите адрес или координаты...",
  token,
  filterLocations = [],
  selectedRegion,
  setAddressValidationError,
  onInputChange,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value?.value || '');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  // Функция для получения подсказок от DaData
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    // Проверка на координаты
    if (isCoordinatesFormat(query)) {
      const { coordinates, error } = parseCoordinates(query);
      if (!error && coordinates) {
        const coordinatesSuggestion = {
          value: query, // сохраняем исходный ввод пользователя (чистые координаты)
          data: {
            geo_lat: coordinates.lat,
            geo_lon: coordinates.lon,
            city_with_type: 'Координаты',
          },
        };
        setSuggestions([coordinatesSuggestion]);
        setIsOpen(true);
        setAddressValidationError(null);
        setLoading(false);
        return; // не идём в API
      } else {
        // Если координаты невалидны — очищаем список и показываем ошибку
        setSuggestions([]);
        setAddressValidationError(error);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      const response = await fetch('https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Token ${token}`
        },
        body: JSON.stringify({
          query: query,
          count: 8,
          locations: filterLocations
        })
      });

      const data = await response.json();

      // Удаляем дубликаты и фильтруем результаты
      const filteredSuggestions = removeDuplicates(data.suggestions || []);
      // - Сортировка результатов 
      const sortedSuggestions = sortSuggestionsByPriority(filteredSuggestions, selectedRegion, query);

      console.log('[+] Selected region: ', selectedRegion);

      setSuggestions(sortedSuggestions);
      setIsOpen(true);
      updatePosition();
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [token, filterLocations, setAddressValidationError]);

  // Обновление позиции выпадающего списка
  const updatePosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, []);

  // Debounced поиск
  useEffect(() => {
    // Не делаем запрос если список закрыт (после выбора адреса)
    if (!isOpen) return;

    const timer = setTimeout(() => {
      fetchSuggestions(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, fetchSuggestions, isOpen]);

  // Синхронизация inputValue с внешним value (для отображения адреса из сделки)
  useEffect(() => {
    if (value?.value !== inputValue) {
      setInputValue(value?.value || '');
    }
  }, [value]);

  // Функция для проверки, являются ли данные координатами
  const isCoordinatesFormat = (input) => {
    const coordinatesRegex = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;
    return coordinatesRegex.test(input.trim());
  };

  // Функция для парсинга координат
  const parseCoordinates = (input) => {
    const [lat, lon] = input.split(',').map(coord => parseFloat(coord.trim()));

    if (isNaN(lat) || isNaN(lon)) {
      return { error: 'Пожалуйста, введите корректные числовые значения' };
    }

    if (lat < -90 || lat > 90) {
      return { error: 'Широта должна быть в диапазоне от -90 до 90' };
    }

    if (lon < -180 || lon > 180) {
      return { error: 'Долгота должна быть в диапазоне от -180 до 180' };
    }

    if (lat === 0 && lon === 0) {
      return { error: 'Широта и долгота не могут быть равны 0' }
    }

    return {
      coordinates: { lat, lon },
      error: null
    };
  };

  // Обработка изменения input
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (onInputChange) {
      onInputChange(newValue);
    }

    if (newValue.length >= 3) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setSuggestions([]);
    }
  };

  // Обработка выбора подсказки
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.value);
    setIsOpen(false);
    setSuggestions([]);

    // Убираем фокус с input чтобы предотвратить повторное открытие
    if (inputRef.current) {
      inputRef.current.blur();
    }

    if (onChange) {
      onChange(suggestion);
    }
  };

  // Обработка прокрутки и изменения размера окна
  useEffect(() => {
    const handlePositionUpdate = () => {
      if (isOpen && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();

        // Проверяем, виден ли input на экране
        const isVisible = rect.top >= 0 && rect.left >= 0 &&
          rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;

        if (!isVisible) {
          // Если input не виден, закрываем список
          setIsOpen(false);
        } else {
          // Если виден, обновляем позицию
          updatePosition();
        }
      }
    };

    // Добавляем обработчики с throttling для производительности
    let ticking = false;
    const throttledUpdate = (event) => {
      // Игнорируем прокрутку внутри выпадающего списка
      if (suggestionsRef.current && suggestionsRef.current.contains(event.target)) {
        return;
      }

      if (!ticking) {
        requestAnimationFrame(() => {
          handlePositionUpdate();
          ticking = false;
        });
        ticking = true;
      }
    };

    if (isOpen) {
      window.addEventListener('scroll', throttledUpdate, true);
      window.addEventListener('resize', throttledUpdate);
    }

    return () => {
      window.removeEventListener('scroll', throttledUpdate, true);
      window.removeEventListener('resize', throttledUpdate);
    };
  }, [isOpen, updatePosition]);

  // Обработка клика вне компонента
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && !inputRef.current.contains(event.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Обработка клавиш
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleBlur = (e) => {
    // Смена стилей (как было)
    e.target.style.borderColor = '#dbe3ef';
    e.target.style.boxShadow = '0 1px 2px rgba(2, 2, 23, 0.05)';

    const trimmed = inputValue.trim();
    // Если введены координаты и они валидны
    if (isCoordinatesFormat(trimmed)) {
      // Если текущее значение уже соответствует выбранному – пропускаем
      if (value && value.value === trimmed) {
        return;
      }

      const { coordinates, error } = parseCoordinates(trimmed);
      if (!error && coordinates) {
        const suggestion = {
          value: trimmed,
          data: {
            geo_lat: coordinates.lat,
            geo_lon: coordinates.lon,
            city_with_type: 'Координаты',
          },
        };
        if (onChange) {
          onChange(suggestion);
        }
        setAddressValidationError(null);
        setIsOpen(false);
        setSuggestions([]);
      } else {
        setAddressValidationError(error);
      }
    }
  };

  // Компонент выпадающего списка
  const SuggestionsList = () => {
    if (!isOpen || suggestions.length === 0) return null;

    return ReactDOM.createPortal(
      <div
        ref={suggestionsRef}
        className="custom-suggestions-container"
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: position.width,
          zIndex: 2147483647,
          background: '#ffffff',
          border: '1px solid rgba(2, 2, 23, 0.08)',
          borderRadius: '10px',
          boxShadow: '0 1px 2px rgba(2, 2, 23, 0.04), 0 20px 40px -18px rgba(2, 2, 23, 0.30)',
          maxHeight: '320px',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '5px',
          scrollBehavior: 'smooth',
          fontFamily: '"Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif'
        }}
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => {
          // Предотвращаем всплытие события прокрутки
          e.stopPropagation();
        }}
      >
        {loading && (
          <div style={{ padding: '8px 10px', color: '#7a8aa3', fontSize: '12px' }}>
            Поиск...
          </div>
        )}
        {suggestions.map((suggestion, index) => {
          const address = suggestion.data;
          return (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className="suggestion-item"
              style={{
                padding: '8px 10px',
                cursor: 'pointer',
                borderRadius: '6px',
                transition: 'background-color 0.15s ease',
                color: '#020217',
                fontWeight: 500,
                fontSize: '13px',
                lineHeight: 1.3,
                backgroundColor: 'transparent'
              }}
            >
              {(() => {
                if (suggestion.data.city_with_type === 'Координаты') {
                  return `Координаты: ${suggestion.value}`;
                }
                return [
                  address.city_with_type,
                  address.settlement_with_type,
                  address.street_with_type,
                  address.house,
                  address.block
                ].filter(Boolean).filter(item => item.trim() !== '').join(', ');
              })()}
            </div>
          );
        })}
      </div>,
      document.body
    );
  };

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={(e) => {
          e.target.style.borderColor = '#1f7fd1';
          e.target.style.boxShadow = '0 0 0 3px rgba(31, 127, 209, 0.18)';

          // Открываем список только если есть подсказки и достаточно текста
          if (suggestions.length > 0 && inputValue.length >= 3) {
            setIsOpen(true);
            updatePosition();
          }
        }}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="form-control"
        autoComplete="off"
        style={{
          width: '100%',
          padding: '10px 14px 10px 35px',
          fontSize: '14px',
          lineHeight: 1.2,
          border: '1px solid #dbe3ef',
          borderRadius: '10px',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          backgroundColor: 'white',
          color: '#020217',
          boxShadow: '0 1px 2px rgba(2, 2, 23, 0.05)'
        }}
      />
      <SuggestionsList />
    </>
  );
};

export default CustomAddressAutocomplete;